import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Leaf } from "@phosphor-icons/react";
import { fetchPublicShare } from "@/lib/api";
import { regionHealth, healthBand, toneColor, ndviStatus, soilMoistureStatus, yieldStatus } from "@/lib/laymen";

/**
 * Public, read-only view of a single region. Accessible by anyone with the
 * share token, no login required. Does NOT expose GPS or operator identity.
 */
export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try { setData(await fetchPublicShare(token)); }
      catch { setErr("not_found"); }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="eid-card p-8 max-w-md text-center space-y-2">
          <h1 className="text-xl font-medium">Link nicht verfügbar</h1>
          <p className="text-sm text-zinc-400">
            Dieser geteilte Link existiert nicht oder wurde widerrufen.
          </p>
          <Link to="/" className="inline-block text-sm underline mt-2">Zur Startseite</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-sm text-zinc-400">Lade…</div>
      </div>
    );
  }

  const m = data.metrics || {};
  const score = regionHealth(m);
  const band = healthBand(score);
  const tone = toneColor(band.level);
  const ns = ndviStatus(m.ndvi_current);
  const ss = soilMoistureStatus(m.soil_moisture_current);
  const ys = yieldStatus(m.yield_forecast_index);

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-10" data-testid="share-view">
      <Helmet>
        <title>{`${data.region?.public_label || "Bodenblick"} · Geteilter Live-Status`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-3xl mx-auto px-6 space-y-5">
        <header className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="share-home-link">
            <span className="p-1.5 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]"><Leaf size={16} /></span>
            <span className="text-sm font-medium">Bodenblick</span>
          </Link>
          <span className="eid-label">Geteilte Ansicht · anonymisiert</span>
        </header>

        <div className="eid-card p-6 flex items-center gap-5" style={{ borderLeft: `3px solid ${tone.dot}` }}>
          <svg width="90" height="90" viewBox="0 0 110 110">
            <circle cx="55" cy="55" r="42" fill="none" stroke="#141414" strokeWidth="8" />
            <circle cx="55" cy="55" r="42" fill="none" stroke={tone.dot} strokeWidth="8"
                    strokeDasharray={2 * Math.PI * 42}
                    strokeDashoffset={2 * Math.PI * 42 * (1 - score / 100)}
                    strokeLinecap="round" transform="rotate(-90 55 55)" />
            <text x="55" y="62" textAnchor="middle" fontSize="22" fontFamily="JetBrains Mono" fill="#FFFFFF">{score}</text>
          </svg>
          <div className="flex-1">
            <div className="eid-label">{data.region?.country} · {data.region?.bundesland}</div>
            <h1 className="text-2xl font-medium">{data.region?.public_label}</h1>
            <p className="text-sm text-zinc-400 mt-1">
              Ökosystem-Gesamtscore: <strong style={{ color: tone.fg }}>{band.label}</strong>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label="NDVI" value={m.ndvi_current?.toFixed?.(2) ?? "–"} sub={ns.label} color={toneColor(ns.level).fg} />
          <Stat label="Bodenfeuchte" value={m.soil_moisture_current?.toFixed?.(2) ?? "–"} sub={ss.label} color={toneColor(ss.level).fg} />
          <Stat label="Ertrags-Index" value={m.yield_forecast_index?.toFixed?.(0) ?? "–"} sub={ys.label} color={toneColor(ys.level).fg} />
        </div>

        <footer className="text-xs text-zinc-500 text-center pt-4">
          Aktualisiert {data.fetched_at ? new Date(data.fetched_at).toLocaleString("de-DE") : "–"} ·
          Daten: Copernicus Sentinel-2 · Open-Meteo · mehr auf{" "}
          <Link to="/" className="underline hover:text-white">bodenblick.com</Link>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="eid-card p-4">
      <div className="eid-label">{label}</div>
      <div className="font-mono text-2xl mt-1" style={{ color }}>{value}</div>
      <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>
    </div>
  );
}
